import { PAYS_UE, PAYS_GZALE } from '../data/countries';
import { getDocumentsByChapter } from './documentsEngine';

export function generateEmailFournisseur({ produit, pays, hsCode, codeActivite }) {
  const isUE = PAYS_UE.includes(pays);
  const isGZALE = PAYS_GZALE.includes(pays);
  const zone = isUE ? 'EU' : isGZALE ? 'GZALE' : 'OTHER';

  // Always generate docs in FR for email — never use page language
  const docs = getDocumentsByChapter(hsCode || '0000', zone, codeActivite || '01', 'fr');
  const extraDocs = docs.fournisseur.filter(d => !['facture','packing','bl','fiche_tech','eur1','cert_gzale','cert_std'].includes(d.id));

  const fr = `Objet : Documents requis pour importation en Algérie — ${produit}

Madame, Monsieur,

Dans le cadre de notre prochaine commande de ${produit}, nous vous informons des documents obligatoires à fournir pour permettre le dédouanement en Algérie.

═══════════════════════════════════════════════════
DOCUMENTS OBLIGATOIRES À PRÉPARER AVANT EXPÉDITION
═══════════════════════════════════════════════════

1. FACTURE COMMERCIALE DÉFINITIVE
   La facture doit OBLIGATOIREMENT mentionner :
   • Date et numéro de facture
   • Nom, adresse complète et pays du vendeur
   • Nom, adresse complète de l'acheteur algérien
   • Description PRÉCISE et DÉTAILLÉE de chaque article
     (⚠️ PAS de mention vague type "marchandises diverses" → rejet douanier)
   • Code SH (HS Code) à 10 chiffres pour chaque article
   • Quantité exacte et unité de mesure
   • Prix unitaire et prix total par article
   • Devise de facturation
   • Incoterm et lieu désigné (ex: FOB Shanghai, CIF Alger)
   • Pays d'ORIGINE DE FABRICATION (≠ pays d'expédition)
   • Votre signature manuscrite et cachet

2. LISTE DE COLISAGE (PACKING LIST)
   Doit mentionner :
   • Référence de la facture correspondante
   • Détail par colis : numéro, contenu exact
   • Poids brut et poids net de chaque colis
   • Dimensions (si pertinent)
   • Nombre total de colis
   • Poids total brut et net
   ⚠️ Doit être strictement cohérente avec la facture

3. FICHE TECHNIQUE DU PRODUIT
   • Dénomination commerciale exacte
   • Caractéristiques techniques détaillées
   • Normes de fabrication (ISO, CE, NF…)
   • Pays de fabrication
   • Nom et adresse du fabricant
   ⚠️ En FRANÇAIS ou ARABE uniquement (pas acceptée en anglais seul)

4. CERTIFICAT D'ORIGINE
${isUE ? `   ⭐ CERTIFICAT EUR.1 (fortement recommandé)
   Ce document nous permet de bénéficier du taux préférentiel 0% de droits
   de douane dans le cadre de l'accord d'association Algérie-UE.
   → À obtenir auprès de votre Chambre de Commerce + visa douane
   → Validité : 10 mois
   → Alternative si montant < 6 000 EUR : déclaration d'origine sur facture
   Mentions requises :
   • Numéro de référence EUR.1
   • Exportateur (nom, adresse, pays)
   • Importateur algérien (nom, adresse)
   • Description marchandise + code HS
   • Pays d'origine
   • Cachet douane du pays exportateur
   • Signature de l'exportateur` : ''}${isGZALE ? `   CERTIFICAT D'ORIGINE GZALE (Zone Arabe de Libre-Échange)
   → Rédigé intégralement en ARABE
   → À obtenir auprès de votre Chambre de Commerce
   Mentions requises :
   • Exportateur et importateur
   • Description marchandise
   • Pays d'origine
   • Cachet Chambre de Commerce + visa autorités` : ''}${!isUE && !isGZALE ? `   CERTIFICAT D'ORIGINE STANDARD
   → À obtenir auprès de votre Chambre de Commerce locale
   → Doit mentionner le pays de FABRICATION réelle (pas le pays d'expédition)` : ''}
${extraDocs.length > 0 ? `
═══════════════════════════════════════════════════
DOCUMENTS SPÉCIFIQUES À CE PRODUIT
═══════════════════════════════════════════════════
${extraDocs.map((d, i) => {
  let text = `${i + 5}. ${d.nom.toUpperCase()}\n   ${d.details}`;
  if (d.mentions && Array.isArray(d.mentions)) {
    text += '\n   Mentions requises :\n' + d.mentions.map(m => `   • ${m}`).join('\n');
  }
  if (d.howto) text += `\n   → ${d.howto}`;
  if (d.warning) text += `\n   ⚠️ ${d.warning}`;
  return text;
}).join('\n\n')}` : ''}

═══════════════════════════════════════════════════
INFORMATIONS IMPORTANTES
═══════════════════════════════════════════════════
• Ne pas expédier avant notre confirmation écrite
• Tous les documents doivent être en version ORIGINALE
• Les fiches techniques doivent être en français ou arabe
• En cas de question, contactez-nous AVANT l'expédition

Cordialement,
[Nom de votre entreprise]
[Adresse]
[Téléphone / Email]`;

  // EN version — generate docs in EN
  const enExtraDocs = extraDocs;

  const en = `Subject: Required documents for customs clearance in Algeria — ${produit}

Dear Sir/Madam,

For our upcoming order of ${produit}, please find below the mandatory documents required for customs clearance in Algeria.

═══════════════════════════════════════════════════
MANDATORY DOCUMENTS — Prepare before shipment
═══════════════════════════════════════════════════

1. COMMERCIAL INVOICE
   Must include the following information:
   • Invoice date and number
   • Seller's full name, address and country
   • Algerian buyer's full name and address
   • PRECISE and DETAILED description of each item
     (⚠️ NO vague descriptions like "miscellaneous goods" → customs rejection)
   • HS Code (10 digits) for each item
   • Exact quantity and unit of measure
   • Unit price and total price per item
   • Currency of invoicing
   • Incoterm and named place (e.g., FOB Shanghai, CIF Algiers)
   • Country of MANUFACTURE (≠ country of shipment)
   • Handwritten signature and company stamp

2. PACKING LIST
   Must include:
   • Reference to corresponding invoice
   • Detail per package: number, exact contents
   • Gross and net weight per package
   • Dimensions (if applicable)
   • Total number of packages
   • Total gross and net weight
   ⚠️ Must be strictly consistent with the invoice

3. TECHNICAL DATASHEET
   • Exact commercial name of the product
   • Detailed technical specifications
   • Manufacturing standards (ISO, CE, NF…)
   • Country of manufacture
   • Manufacturer's full name and address
   ⚠️ Must be in FRENCH or ARABIC (English only NOT accepted)

4. CERTIFICATE OF ORIGIN
${isUE ? `   ⭐ EUR.1 MOVEMENT CERTIFICATE (strongly recommended)
   This document allows us to benefit from 0% customs duty rate
   under the Algeria-EU Association Agreement.
   → Obtain from your Chamber of Commerce + customs visa
   → Validity: 10 months
   → Alternative if amount < EUR 6,000: origin declaration on invoice
   Required information:
   • EUR.1 reference number
   • Exporter (name, address, country)
   • Algerian importer (name, address)
   • Goods description + HS code
   • Country of origin
   • Customs stamp of exporting country
   • Exporter's signature` : ''}${isGZALE ? `   GAFTA CERTIFICATE OF ORIGIN (Arab Free Trade Zone)
   → Must be written entirely in ARABIC
   → Obtain from your Chamber of Commerce
   Required information:
   • Exporter and importer details
   • Goods description
   • Country of origin
   • Chamber of Commerce stamp + authorities visa` : ''}${!isUE && !isGZALE ? `   STANDARD CERTIFICATE OF ORIGIN
   → Obtain from your local Chamber of Commerce
   → Must state the actual country of MANUFACTURE (not country of shipment)` : ''}
${enExtraDocs.length > 0 ? `
═══════════════════════════════════════════════════
PRODUCT-SPECIFIC DOCUMENTS
═══════════════════════════════════════════════════
${enExtraDocs.map((d, i) => {
  let text = `${i + 5}. ${d.nom.toUpperCase()}\n   ${d.details}`;
  if (d.mentions && Array.isArray(d.mentions)) {
    text += '\n   Required information:\n' + d.mentions.map(m => `   • ${m}`).join('\n');
  }
  if (d.howto) text += `\n   → ${d.howto}`;
  if (d.warning) text += `\n   ⚠️ ${d.warning}`;
  return text;
}).join('\n\n')}` : ''}

═══════════════════════════════════════════════════
IMPORTANT INFORMATION
═══════════════════════════════════════════════════
• Do NOT ship before our written confirmation
• All documents must be in ORIGINAL form
• Technical datasheets must be in French or Arabic
• If you have any questions, contact us BEFORE shipment

Best regards,
[Your company name]
[Address]
[Phone / Email]`;

  return { fr, en };
}
